import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Button, Paper, Typography, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import NebraskaLogo from '../assets/png/N-Logo.png';
import { usePatient } from "../context/patientContext.jsx";
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ScienceIcon from '@mui/icons-material/Science';

import '../styles/dashboard.css';
import { useTranslation } from 'react-i18next';

function Dashboard() {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const isCypress = localStorage.getItem('isCypress') === 'true';
  const { resetDraft, uploadQueue, isInitialized } = usePatient();
  const [view, setView] = useState('home');
  const { loginWithRedirect } = useAuth0();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const entries = uploadQueue?.length ?? 0;
  const [open, setOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showQualtrics, setShowQualtrics] = useState(false);

  useEffect(() => {
    if (!isInitialized) return;
    resetDraft();
    sessionStorage.removeItem("showQualtrics");
    sessionStorage.removeItem("questionnaireAnswers");
    sessionStorage.removeItem("screeningResults");
    sessionStorage.removeItem("diagnosticResults");
    sessionStorage.removeItem("screeningReferrals");
    sessionStorage.removeItem("diagnosticReferrals");

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // The resetDraft() method is not a dependency for this useEffect. We just want
    //   to run it a single time when the dashboard is reached to make sure that the
    //   patient info is cleared upon hitting the dashboard. If we include it in the
    //   dependency array, React will reach maximum update depth, as it tries to
    //   repeatedly re-render the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  return (
    <Box className="root">
      <Drawer
        className="drawer"
        variant="permanent"
        anchor="left"
      >
        <Box className="logoContainer">
          <img
            src={NebraskaLogo}
            alt="Nebraska Logo"
            className="logo"
          />
        </Box>
        <List>
          <ListItem disablePadding>
            <ListItemButton selected={view === 'home'} onClick={() => setView('home')} className="homeBtn">
              <HomeIcon className="iconMr" />
              <ListItemText primary={t('dashboard.home')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isOffline}
              selected={view === 'filtered'}
              onClick={() => setOpen(!open)}
            >
              <SearchIcon className="iconMr" />
              <ListItemText primary={t('dashboard.filteredSearch')} />
              {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>

              <ListItemButton
                className="nestedBtn"
                onClick={() => loginWithRedirect({
                  authorizationParams: { prompt: 'login' },
                  appState: { returnTo: '/Search' },
                })}
              >
                <PersonSearchIcon className="iconMr" />
                <ListItemText primary={t('dashboard.patientSearch')} />
              </ListItemButton>

              <ListItemButton
                className="nestedBtn"
                onClick={() => loginWithRedirect({
                  authorizationParams: { prompt: 'login' },
                  appState: { returnTo: '/testSearch' },
                })}
              >
                <ScienceIcon className="iconMr" />
                <ListItemText primary={t('dashboard.testSearch')} />
              </ListItemButton>

            </List>
          </Collapse>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isOffline}
              data-cy="pending_entries_tab"
              onClick={() => {
                if (isCypress) {
                  navigate('/pendingEntries');
                  return;
                }
                loginWithRedirect({
                  authorizationParams: { prompt: 'login' },
                  appState: { returnTo: '/pendingEntries' },
                });
              }}
            >
              <SearchIcon className="iconMr" />
              <ListItemText primary={t('dashboard.pendingEntries', { count: entries })} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            {process.env.NODE_ENV === 'development' && <ListItemButton onClick={() => navigate('/calibration')}>
              <ListItemText primary='Perform Headphone Calibration' />
            </ListItemButton>}
          </ListItem>
        </List>
      </Drawer>
      {view === 'home' ? (
        <Box component="main" className="mainHome">
          <Box className="homeContent">

            {/* Connectivity Banner */}
            <Paper
              elevation={2}
              className={`banner ${isOffline ? "bannerOffline" : "bannerOnline"}`}
            >
              {isOffline ? (
                <WifiOffIcon className="bannerIcon bannerIconOffline" />
              ) : (
                <WifiIcon className="bannerIcon bannerIconOnline" />
              )}

              <Box>
                <Typography className="bannerTitle">
                  {isOffline ? t('dashboard.offlineTitle') : t('dashboard.onlineTitle')}
                </Typography>

                <Typography variant="body2" className="bannerSubtitle">
                  {isOffline
                    ? t('dashboard.offlineBody')
                    : t('dashboard.onlineBody')}
                </Typography>
              </Box>
            </Paper>

            {/* Primary Action */}
            <Button
              variant="contained"
              data-cy="begin_test_btn"
              onClick={() => { setConsentChecked(false); setDialogOpen(true); }}
              size="large"
            >
              {t('dashboard.beginTest')}
            </Button>

            <FormControlLabel
              control={
                <Checkbox
                  data-cy="qualtrics_checkbox"
                  checked={showQualtrics}
                  onChange={(e) => {
                    setShowQualtrics(e.target.checked);
                    sessionStorage.setItem("showQualtrics", e.target.checked);
                  }}
                />
              }
              label={t('qualtrics.participate')}
            />

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
              <DialogTitle>{t('dashboard.consent.title')}</DialogTitle>

              <DialogContent dividers>
                <Typography variant="body1">
                  {t('dashboard.consent.educationalPurposes')}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.consent.notMedicalDiagnosis')}
                </Typography>

                <Typography variant="body1">
                  {t('dashboard.consent.agreeToResearch')}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.consent.personalInfoNotDisclosed')}
                </Typography>

                <Box className="consentCheckboxRow">
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-cy="consent_checkbox"
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                      />
                    }
                    label={t('dashboard.consent.checkbox')}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button
                  data-cy="consent_continue_btn"
                  disabled={!consentChecked}
                  onClick={() => {
                    setDialogOpen(false);
                    navigate('/signup', { state: { from: location.pathname } });
                  }}
                  variant="contained"
                >
                  {t('common.continue')}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      ) : (
        <Box component="main" className="mainOther">
          <Paper
            elevation={3}
            className="placeholderPaper"
          >
          </Paper>
        </Box>

      )}
    </Box>
  );
}
export default Dashboard;
